const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/appointmentConfirmation.ts';
import { DocumentReadWorkItem, WorkItem } from '@cistec/common_web/defs/kisim';
import { IActionHandlersMap, KisApiAction, makeReducer, ReducerState } from '@cistec/common_web/defs/actionTypes';
import {
    addRequestActionHandlers,
    defaultKisApiActionSuccess,
    getData,
    getDataFromPayloadWithListObject,
    isLoading,
    useRemoteData2,
} from '@cistec/common_web/utils/redux4Requests';
import {
    actionLoadAppointmentConfirmations,
    CREATE_APPOINTMENT_CONFIRMATION,
    LOAD_APPOINTMENT_CONFIRMATIONS,
} from 'App/actions/appointmentConfirmation';
import { PatAppStoreState } from './setup/rootReducer';
import { createSelector } from 'reselect';
import { getPatNrFromSession } from 'App/utils/sessionStorage';

const KEY_CONFIRMATION_MAP = 'map';
const KEY_CONFIRMATION_ARRAY = 'array';

const handlers: IActionHandlersMap = {};

function handleLoadAppointmentConfirmationsSuccess(state: ReducerState, action: KisApiAction) {
    const workItems = getDataFromPayloadWithListObject(action);
    workItems.forEach((workItem: DocumentReadWorkItem) => {
        const terminNr = workItem.TKEY.split(':')[1];
        if (terminNr) {
            state = defaultKisApiActionSuccess(state, workItem, [KEY_CONFIRMATION_MAP, terminNr]);
        }
    });

    // Handle successful load of data, but the data is stored somewhere else
    state = defaultKisApiActionSuccess(state, undefined, [KEY_CONFIRMATION_ARRAY]);
    return state;
}

addRequestActionHandlers(
    handlers,
    LOAD_APPOINTMENT_CONFIRMATIONS,
    [KEY_CONFIRMATION_ARRAY],
    undefined,
    handleLoadAppointmentConfirmationsSuccess
);

function handleSaveAppointmentConfirmationSuccess(state: ReducerState, action: KisApiAction) {
    // Service for marking PatReport as read returns result prop containing WorkItem
    // Service for saving WorkItem returns WorkItem directly in payload
    const workItem = action.payload.result || action.payload;
    state = defaultKisApiActionSuccess(state, workItem, [KEY_CONFIRMATION_MAP, action.meta.appointment.TERMNR]);
    return state;
}

const getKeysForConfirmAppointment = (action: KisApiAction) => {
    return [KEY_CONFIRMATION_MAP, action.meta.appointment.TERMNR];
};

addRequestActionHandlers(
    handlers,
    CREATE_APPOINTMENT_CONFIRMATION,
    getKeysForConfirmAppointment,
    undefined,
    handleSaveAppointmentConfirmationSuccess
);

export const appointmentConfirmationReducer = makeReducer(handlers);

// SELECTORS ==========================

const selectReducerState = (state: PatAppStoreState) => state.appointmentConfirmation;

const makeSelectAppointmentConfirmation = (terminNr: string) =>
    createSelector([selectReducerState], (state: ReducerState): WorkItem | undefined => {
        return getData(state, [KEY_CONFIRMATION_MAP, terminNr]);
    });

export const makeSelectIsLoadingAppointmentConfirmation = (terminNr: string) =>
    createSelector([selectReducerState], (state: ReducerState) => {
        return isLoading(state, [KEY_CONFIRMATION_MAP, terminNr]);
    });

// HOOKS ====================

export const useAppointmentConfirmation = (terminNr: string) => {
    const patNr = getPatNrFromSession();

    return useRemoteData2('appointmentConfirmation', {
        canRequest: !!patNr,
        request: dispatch => dispatch(actionLoadAppointmentConfirmations()),
        keys: [KEY_CONFIRMATION_ARRAY],
        select: makeSelectAppointmentConfirmation(terminNr),
    });
};
